import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  useConversationControls,
  useConversationStatus,
  useConversationMode,
  useConversationInput,
} from '@elevenlabs/react';
import { motion } from 'framer-motion';
import SoftButton from './SoftButton';
import { playUiClick } from './humanityUiSounds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ElevenLabsEntityVoice — the artwork-wrapped voice driver.
 *
 * Must be rendered inside a <ConversationProvider> from @elevenlabs/react.
 * Renders only the in-conversation surface:
 *   - poetic status copy ("the entity is listening", etc.)
 *   - bottom "Finish Conversation" CTA when connected
 *
 * Mute is rendered separately by <MuteToggle> in the page top-right. The
 * initial Start CTA is collapsed into the page-level "Interact" button —
 * this component auto-starts on mount when `autoStart` is true.
 *
 * Props:
 *   agentId             — ElevenLabs public agent id
 *   sessionId           — humanity_sessions.id (passed as userId)
 *   dynamicVariables    — record sent as ElevenLabs dynamicVariables
 *   autoStart           — start the session automatically once mounted
 *   onVoiceStateChange  — fn(voiceState)
 *   onConversationEnded — fn()
 *   onConversationIdAttached — fn(conversationId)
 */
export default function ElevenLabsEntityVoice({
  agentId,
  sessionId,
  dynamicVariables,
  autoStart = false,
  onVoiceStateChange,
  onConversationEnded,
  onConversationIdAttached,
}) {
  const { startSession, endSession, getId } = useConversationControls();
  const { status, message: statusMessage } = useConversationStatus();
  const { isSpeaking, isListening } = useConversationMode();
  const { isMuted } = useConversationInput();

  const [requesting, setRequesting] = useState(false);
  const [micError, setMicError] = useState('');
  const startedRef = useRef(false);
  const autoStartedRef = useRef(false);
  const attachedRef = useRef(false);

  // ── attach ElevenLabs conversation id once the SDK has it ─────
  useEffect(() => {
    if (status !== 'connected' || attachedRef.current || !sessionId) return undefined;
    let cancelled = false;
    let convId = '';
    try {
      convId = (getId && getId()) || '';
    } catch { /* ignore */ }
    if (!convId) {
      console.warn('[HumanityMirror] connected but getId() returned empty');
      return undefined;
    }
    attachedRef.current = true;
    try { localStorage.setItem('humanity_conversation_id', convId); } catch { /* ignore */ }
    (async () => {
      try {
        await axios.post(
          `${API}/humanity/session/${sessionId}/attach-conversation`,
          { elevenlabs_conversation_id: convId },
          { timeout: 8000 },
        );
        if (!cancelled) onConversationIdAttached?.(convId);
      } catch (err) {
        attachedRef.current = false;
        console.warn('[HumanityMirror] attach-conversation failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [status, sessionId, getId, onConversationIdAttached]);

  // ── surface SDK status==='error' as visitor-facing error ─────
  useEffect(() => {
    if (status === 'error' && !micError) {
      console.error('[HumanityMirror] SDK reported status=error', statusMessage || '(no message)');
      setMicError('start_failed');
    }
  }, [status, statusMessage, micError]);

  // ── derive poetic voice state ────────────────────────────────
  useEffect(() => {
    let vs = 'sleeping';
    if (micError) vs = 'error';
    else if (status === 'connecting' || requesting) vs = 'waking';
    else if (status === 'connected') {
      if (isMuted) vs = 'muted';
      else if (isSpeaking) vs = 'speaking';
      else if (isListening) vs = 'listening';
      else vs = 'listening';
    } else if (status === 'error') vs = 'error';
    onVoiceStateChange?.(vs);
  }, [status, isSpeaking, isListening, isMuted, requesting, micError, onVoiceStateChange]);

  const isConnected = status === 'connected';
  const isConnecting = status === 'connecting' || requesting;

  async function handleStart() {
    setMicError('');
    setRequesting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Release immediately — the SDK manages its own tracks.
      stream.getTracks().forEach((t) => t.stop());
    } catch (err) {
      console.error('[HumanityMirror] microphone permission denied', err);
      setMicError('mic_denied');
      setRequesting(false);
      onVoiceStateChange?.('error');
      return;
    }
    try {
      const sessionOptions = {
        agentId,
        userId: sessionId || `humanity-${Date.now()}`,
        connectionType: 'websocket',
        ...(dynamicVariables ? { dynamicVariables } : {}),
      };
      const maybeId = await startSession(sessionOptions);
      if (typeof maybeId === 'string' && maybeId) {
        try { localStorage.setItem('humanity_conversation_id', maybeId); } catch { /* ignore */ }
      }
      startedRef.current = true;
    } catch (err) {
      console.error('[HumanityMirror] startSession failed', err);
      setMicError('start_failed');
      onVoiceStateChange?.('error');
    } finally {
      setRequesting(false);
    }
  }

  // ── auto-start once when the parent signalled "Interact" ─────
  useEffect(() => {
    if (!autoStart || autoStartedRef.current) return;
    if (status === 'connected' || status === 'connecting' || requesting) return;
    autoStartedRef.current = true;
    handleStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, status]);

  async function handleEnd() {
    playUiClick();
    try { await endSession(); } catch (err) {
      console.error('[HumanityMirror] endSession failed', err);
    }
    startedRef.current = false;
    attachedRef.current = false;
    onVoiceStateChange?.('ended');
    onConversationEnded?.();
  }

  // ── poetic status copy ───────────────────────────────────────
  let statusCopy = '';
  if (micError === 'mic_denied' || micError === 'start_failed') statusCopy = 'The voice did not open.';
  else if (isConnecting) statusCopy = 'The entity is waking.';
  else if (isConnected) {
    if (isMuted) statusCopy = 'Your voice is hidden.';
    else if (isSpeaking) statusCopy = 'The entity is speaking.';
    else statusCopy = 'The entity is listening.';
  }

  return (
    <div className="flex flex-col items-center gap-7 w-full" data-testid="elevenlabs-entity-voice">
      {/* Quiet status line above the action */}
      <motion.p
        key={statusCopy || 'idle'}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="font-mono tracking-[0.32em] uppercase text-[10px] sm:text-[11px] text-white/55 min-h-[14px]"
        data-testid="entity-status-copy"
      >
        {statusCopy}
      </motion.p>

      {/* Bottom action: Finish only. Start was collapsed into the page-level
          Interact CTA (which renders this component with autoStart=true). */}
      {isConnected ? (
        <SoftButton
          variant="danger"
          size="xl"
          onClick={handleEnd}
          testId="finish-voice-conversation"
          className="humanity-cta-pulse humanity-finish-cta"
        >
          Finish Conversation
        </SoftButton>
      ) : micError ? (
        <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-rose-300/75" data-testid="voice-error">
          The voice did not open.
        </p>
      ) : null}
    </div>
  );
}
